import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  Home as HomeIcon,
  TravelExplore as ExploreIcon,
  PostAdd as PostAddIcon,
  VideoLibrary as VideoLibraryIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  MapOutlined as MapOutlinedIcon,
  Bookmark as BookmarkIcon,
  Star as StarIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  ChevronRight as ChevronRightIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  Chat as ChatIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { logout } from '../../store/slices/AuthSlice';
import { clearFcmToken } from '../../store/slices/FcmSlice';
import { setUser } from '../../store/slices/User';
import { unregisterFcmToken } from '../../services/firebaseMessaging';
import { signOutGoogle } from '../../services/googleAuth';
import { clearUserCache } from '../../services/users';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import {
  getOrCreateHelpCenterChat,
  HelpCenterChat,
} from '../../services/helpCenterService';

const HamburgerMenu: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const authType = useSelector((state: any) => state.auth.authType);
  const [adminChatLoading, setAdminChatLoading] = useState(false);

  // Check if phone is verified based on the user data structure
  const isPhoneVerified = user?.user?.phone?.isVerified === true;
  const isEmailVerified = user?.user?.emailVerification?.isVerified === true;
  const currentUserId = user?.user?._id || user?.user?.id;

  const handleLogout = async () => {
    try {
      // If user is logged in via Google, sign out from Google as well
      if (authType === 'google') {
        await signOutGoogle();
      }

      // Unregister FCM token
      await unregisterFcmToken();

      // Clear any memoized user cache (usernames, profiles)
      clearUserCache();

      // Redux logout & clear token
      dispatch(clearFcmToken());
      dispatch(logout());
      dispatch(setUser(null));
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still perform local logout even if Google logout fails
      await unregisterFcmToken();
      clearUserCache();
      dispatch(clearFcmToken());
      dispatch(logout());
      dispatch(setUser(null));
      navigate('/login');
    }
  };

  const handleAdminChat = async () => {
    if (!currentUserId) {
      window.alert('Please log in to access Admin Chat.');
      return;
    }

    setAdminChatLoading(true);

    try {
      const adminChat = await getOrCreateHelpCenterChat(
        currentUserId,
        user?.user?.username || 'User',
      );

      const adminParticipantId = resolveAdminParticipantId(adminChat, currentUserId);
      const adminDisplayName = resolveAdminDisplayName(adminChat);

      navigate(`/chat/${adminChat.id}`, {
        state: {
          chatId: adminChat.id,
          otherUserName: adminParticipantId,
          otherUserDisplayName: adminDisplayName,
          source: 'hamburgerMenu',
        },
      });
    } catch (error) {
      console.error('Error setting up admin chat:', error);
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to open Admin Chat. Please try again.';
      window.alert(errorMessage);
    } finally {
      setAdminChatLoading(false);
    }
  };

  const resolveAdminParticipantId = (chat: HelpCenterChat, currentUser: string): string => {
    const participants = Array.isArray(chat.participants) ? chat.participants : [];

    const participantId =
      participants.find(
        (participant) =>
          typeof participant === 'string' &&
          participant.trim().length > 0 &&
          participant !== currentUser,
      ) || 'admin';

    return participantId;
  };

  const resolveAdminDisplayName = (chat: HelpCenterChat): string => {
    const metadata =
      typeof chat.metadata === 'object' && chat.metadata !== null
        ? (chat.metadata as Record<string, unknown>)
        : {};

    const displayName =
      typeof metadata.adminDisplayName === 'string' && metadata.adminDisplayName.trim().length > 0
        ? metadata.adminDisplayName
        : 'Admin Chat';

    return displayName;
  };

  // Main navigation menu items
  const mainMenuItems = [
    { 
      icon: <HomeIcon sx={{ color: '#008387' }} />, 
      label: 'Home', 
      action: () => navigate('/')
    },
    { 
      icon: <ExploreIcon sx={{ color: '#008387' }} />, 
      label: 'Trips', 
      action: () => navigate('/trips')
    },
    { 
      icon: <PostAddIcon sx={{ color: '#008387' }} />, 
      label: 'Create Post', 
      action: () => navigate('/createpost')
    },
    { 
      icon: <VideoLibraryIcon sx={{ color: '#008387' }} />, 
      label: 'Shorts', 
      action: () => navigate('/shorts')
    },
  ];

  // Profile related menu items
  const profileMenuItems = [
    { 
      icon: <MapOutlinedIcon sx={{ color: '#008387' }} />, 
      label: 'Discovered', 
      action: () => navigate('/my-places')
    },
    { 
      icon: <FavoriteIcon sx={{ color: '#008387' }} />, 
      label: 'Favourite', 
      action: () => navigate('/favorite-places')
    },
    { 
      icon: <StarIcon sx={{ color: '#008387' }} />, 
      label: 'Reviews', 
      action: () => navigate('/my-reviews')
    },
    { 
      icon: <BookmarkIcon sx={{ color: '#008387' }} />, 
      label: 'Saved post', 
      action: () => navigate('/saved-posts')
    },
  ];

  // Settings menu items
  const settingsMenuItems = [
    { 
      icon:
      //  isPhoneVerified ? 
      //   <CheckIcon sx={{ color: '#4CAF50' }} /> : 
        <PhoneIcon sx={{ color: '#008387' }} /> ,
      label: 'Verify Phone Number',
      // isPhoneVerified ? 'Phone Verification' : 'Verify Phone Number', 
      action: () => navigate('/phone-verification')
    },
    { 
      icon:
        <EmailIcon sx={{ color: '#008387' }} /> ,
      label: isEmailVerified ? 'Email Verification' : 'Verify Email',
      action: () => navigate('/email-verification')
    },
    { 
      icon: <LockIcon sx={{ color: '#008387' }} />, 
      label: 'Change password', 
      action: () => navigate('/change-password')
    },
    { 
      icon: <EditIcon sx={{ color: '#008387' }} />, 
      label: 'Edit Profile', 
      action: () => navigate('/profile/edit')
    },
    { 
      icon: <LockIcon sx={{ color: '#008387' }} />, 
      label: 'Privacy & Visibility', 
      action: () => navigate('/account-privacy')
    },
    { 
      icon: <ChatIcon sx={{ color: '#008387' }} />, 
      label: 'Admin Chat', 
      action: handleAdminChat,
      disabled: adminChatLoading
    },
    { 
      icon: <SettingsIcon sx={{ color: '#008387' }} />, 
      label: 'Help Center', 
      action: () => navigate('/help-center')
    },
    { 
      icon: <PersonOffIcon sx={{ color: '#008387' }} />, 
      label: 'Request Account deactivation', 
      action: () => navigate('/deactivate-account')
    },
  ];

  const MenuSection: React.FC<{ 
    title?: string; 
    items: Array<{ 
      icon: React.ReactNode; 
      label: string; 
      action: () => void; 
      disabled?: boolean;
    }> 
  }> = ({ title, items }) => (
    <Box>
      <List>
        {items.map((item, index) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton 
              onClick={item.action}
              sx={{ 
                borderRadius: '12px',
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: '#F5F5F5'
                }
              }}
              disabled={item.disabled}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{ 
                  '& .MuiListItemText-primary': {
                    color: '#49454F',
                    fontWeight: 500
                  }
                }}
              />
              <ChevronRightIcon sx={{ color: '#9E9E9E' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ 
      bgcolor: '#fff', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
        <TopNavBar title='Profile' canGoBack/>

      <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>

        {/* Main Navigation */}
        {/* <MenuSection items={mainMenuItems} />

        <Divider sx={{ my: 2 }} /> */}

        {/* Profile Options */}
        <MenuSection items={profileMenuItems} />

        <Divider sx={{ my: 2 }} />

        {/* Settings Options */}
        <MenuSection items={settingsMenuItems} />

        <Divider sx={{ my: 2 }} />

        {/* Logout */}
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={handleLogout}
              sx={{ 
                borderRadius: '12px',
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: '#FFEBEE'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon sx={{ color: '#F44336' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Logout"
                sx={{ 
                  '& .MuiListItemText-primary': {
                    color: '#F44336',
                    fontWeight: 500
                  }
                }}
              />
              <ChevronRightIcon sx={{ color: '#9E9E9E' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={adminChatLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default HamburgerMenu;
